import { encodeWav, type StereoAudio } from './audio-data';

const MP3_RATES = [32000, 44100, 48000];

export function wavBlob(a: StereoAudio): Blob {
  return new Blob([encodeWav(a)], { type: 'audio/wav' });
}

/** MP3 only supports up to 48 kHz; resample first if the device runs higher. */
async function toMp3Rate(a: StereoAudio): Promise<StereoAudio> {
  if (MP3_RATES.includes(a.sampleRate)) return a;
  const target = 48000;
  const frames = Math.ceil((a.left.length * target) / a.sampleRate);
  const ctx = new OfflineAudioContext(2, frames, target);
  const buf = ctx.createBuffer(2, a.left.length, a.sampleRate);
  buf.copyToChannel(a.left, 0);
  buf.copyToChannel(a.right, 1);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(ctx.destination);
  src.start();
  const out = await ctx.startRendering();
  return { sampleRate: target, left: out.getChannelData(0).slice(), right: out.getChannelData(1).slice() };
}

export async function mp3Blob(a: StereoAudio, kbps = 320): Promise<Blob> {
  const src = await toMp3Rate(a);
  const worker = new Worker(new URL('./mp3.worker.ts', import.meta.url), { type: 'module' });
  try {
    return await new Promise<Blob>((resolve, reject) => {
      worker.onmessage = (e: MessageEvent<Blob>) => resolve(e.data);
      worker.onerror = (e) => reject(new Error(e.message || 'MP3 encoding failed'));
      worker.postMessage({ sampleRate: src.sampleRate, left: src.left, right: src.right, kbps });
    });
  } finally {
    worker.terminate();
  }
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
